/**
 * NotificationsPage (Phase P1) — the in-app feed of customer actions.
 *
 * Lists newest-first with a type icon/label, title, body, timestamp, and a jump
 * to the related lead. Clicking a row marks it read, one at a time — there is
 * deliberately no bulk clear; see the note beside the header.
 * badge. Types rendered now: estimate_accepted, document_completed,
 * dvp_accepted, contractor_approved, move_booked, inventory_submitted,
 * inventory_change_requested.
 *
 * A TYPE WITH NO ENTRY IN TYPE_META STILL RENDERS — as a grey bell labelled
 * "Notification", via fallbackMeta. That is the safety net, not the plan: the
 * label is the only thing telling a rep at a glance which of these rows is a
 * signature and which is a client waiting on them, and "Notification" tells
 * them nothing. Add the entry in the same change that starts writing the type.
 */

import React from 'react';
import { CRM } from '../lib/theme';
import { useApi, useApiClient } from '../hooks/useApi';
import { useUrlParam, useOnChange } from '../hooks/useListState';
import { ClearFilters } from './LeadsPage';
import { formatDateTime } from '../lib/utils';
import { Icons } from '../lib/icons';
import { MissedCallLookup } from './MissedCallLookup';

const PAGE_SIZE = 50;

const C = { ...CRM };

// Per-type presentation. Extend this map as new notification kinds arrive.
const TYPE_META: Record<string, { Icon: React.ComponentType<any>; label: string; color: string }> = {
  estimate_accepted: { Icon: Icons.accepted, label: 'Estimate Accepted', color: 'var(--crm-green)' },
  document_signed: { Icon: Icons.signature, label: 'Document Signed', color: 'var(--crm-accent)' },
  // What the portal actually writes when a client finishes a document. The
  // 'document_signed' key above was reserved for it long before anything emitted
  // one; both are mapped rather than renamed, because a notification already in
  // the table under either key must keep rendering.
  document_completed: { Icon: Icons.signature, label: 'Document Signed', color: 'var(--crm-accent)' },
  // Green, like estimate_accepted: a client bought something. The premium still
  // has to reach an invoice, which is what the body text says.
  dvp_accepted: { Icon: Icons.money, label: 'Coverage Accepted', color: 'var(--crm-green)' },
  contractor_approved: { Icon: Icons.accepted, label: 'Contractor Approved', color: 'var(--crm-accent)' },
  move_booked: { Icon: Icons.calendar, label: 'Move Booked', color: 'var(--crm-amber)' },
  /**
   * P5a. Green, like estimate_accepted and dvp_accepted: the client did the
   * thing we asked them to do. It is still a TASK — nothing reaches the
   * estimate until a rep reviews the list and presses Apply — which is what the
   * body text says.
   *
   * 'inventory_provided' used to sit here, reserved for this and never written
   * by anything. Two keys for one event is how a feed ends up filtering on the
   * one nothing emits, so the unused name is gone rather than kept beside the
   * real one.
   */
  inventory_submitted: { Icon: Icons.box, label: 'Inventory Submitted', color: 'var(--crm-green)' },
  // Amber, and deliberately not green: somebody is BLOCKED until a rep reopens
  // their list. It reads as a job to do, next to the notification that reads as
  // news.
  inventory_change_requested: { Icon: Icons.edit, label: 'Inventory Change Requested', color: 'var(--crm-amber)' },
  // 073: a 1–3 star rating on the feedback page. Red: a client is unhappy and
  // management should read it. (4–5 stars are a feed line only.)
  feedback_low: { Icon: Icons.star, label: 'Low Rating', color: 'var(--crm-red)' },
  // 'email_received' deliberately has NO entry: nothing writes it, and the server
  // filters the rows that already exist. Unread client mail lives in the Messages
  // badge, which can empty as it is worked; the bell cannot.
  // L1a. Green like the other "a client did something" rows: a lead posted
  // itself and is sitting unclaimed. The New Leads badge counts it; this says
  // where it came from, which the badge cannot.
  lead_received: { Icon: Icons.newLead, label: 'New Lead Received', color: 'var(--crm-green)' },
  // RingCentral, written only before migration 068 moved missed calls and
  // inbound texts to Messages. Kept so the old rows still render.
  call_missed: { Icon: Icons.phone, label: 'Missed Call', color: 'var(--crm-red)' },
  // An inbound text on a lead (step 5). Accent: the client wrote, a reply is
  // due. One per text; clicking opens the lead.
  text_received: { Icon: Icons.message, label: 'Text Received', color: 'var(--crm-accent)' },
};
const fallbackMeta = { Icon: Icons.notification, label: 'Notification', color: 'var(--crm-muted)' };

interface NotificationsPageProps {
  onSelectLead: (leadId: string) => void;
  onChanged?: () => void; // let the shell refresh its unread badge
}

export function NotificationsPage({ onSelectLead, onChanged }: NotificationsPageProps) {
  const api = useApiClient();
  // Full-history controls (N3 Item 4): filter by unread + type, 50 at a time.
  // Reading is deliberate — visiting this page does NOT auto-mark-read.
  // Filters, paging and counts run on the server (paging 4/4): the list used
  // to be the first 500 rows, filtered here.
  // In the URL (hooks/useListState), so opening a lead and coming back keeps
  // them: ?unread=1, ?type=, and ?shown= for how far "Load more" had got.
  const [unreadParam, setUnreadParam] = useUrlParam('unread');
  const unreadOnly = unreadParam === '1';
  const setUnreadOnly = (on: boolean) => setUnreadParam(on ? '1' : '');
  const [typeFilter, setTypeFilter] = useUrlParam('type');
  const [shownParam, setShownParam] = useUrlParam('shown', String(PAGE_SIZE));
  const visible = Math.max(PAGE_SIZE, parseInt(shownParam, 10) || PAGE_SIZE);
  const setVisible = (n: number) => setShownParam(String(n));
  useOnChange(() => setVisible(PAGE_SIZE), [unreadOnly, typeFilter]);
  const anyFilter = unreadOnly || !!typeFilter || visible > PAGE_SIZE;
  const clearFilters = () => { setUnreadOnly(false); setTypeFilter(''); setVisible(PAGE_SIZE); };

  const { data, refetch } = useApi(
    async () => (await api.getNotifications({ limit: visible, unread: unreadOnly, type: typeFilter })).data
      || { notifications: [], total: 0, unreadCount: 0, types: [] as string[] },
    { dependencies: [visible, unreadOnly, typeFilter] }
  );

  const shown = data?.notifications ?? [];
  const total = data?.total ?? 0;
  const unreadCount = data?.unreadCount ?? 0;
  const types = data?.types ?? [];

  const refresh = () => {
    refetch();
    onChanged?.();
  };

  const openLead = async (n: any) => {
    try {
      if (!n.read_at) await api.markNotificationRead(n.id);
    } catch {
      /* non-fatal — still navigate */
    }
    refresh();
    if (n.lead_id) onSelectLead(n.lead_id);
    // A missed call from a number on no lead: the number and a lead search.
    else if (n.type === 'call_missed' && n.ringcentral_call_id) setLookupCallId(n.ringcentral_call_id);
  };
  const [lookupCallId, setLookupCallId] = React.useState<string | null>(null);

  const markRead = async (e: React.MouseEvent, n: any) => {
    e.stopPropagation();
    try {
      await api.markNotificationRead(n.id);
      refresh();
    } catch {
      /* ignore */
    }
  };

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
      {lookupCallId && (
        <MissedCallLookup callId={lookupCallId} onClose={() => { setLookupCallId(null); refresh(); }} onOpenLead={onSelectLead} />
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        {/* No "mark all read". Marking one read says a person has seen it; one
            press that clears forty cannot say that, and read_at is a single
            column on a company-scoped row — so a bulk clear answered for every
            admin at once, not just whoever pressed it. Same reasoning as unread
            email, and the same conclusion. */}
        <div style={{ fontSize: '15px', fontWeight: 700, color: C.text }}>
          Notifications {unreadCount > 0 && <span style={{ color: C.amber }}>· {unreadCount} unread</span>}
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: C.muted, cursor: 'pointer' }}>
          <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} />
          Unread only
        </label>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          style={{ padding: '6px 9px', background: C.panel2, border: `1px solid ${C.border}`, borderRadius: '6px', color: C.text, fontSize: '12px', cursor: 'pointer' }}
        >
          <option value="">All types</option>
          {types.map((t) => <option key={t} value={t}>{TYPE_META[t]?.label ?? t}</option>)}
        </select>
        <span style={{ fontSize: '11px', color: C.dim }}>{shown.length.toLocaleString()} of {total.toLocaleString()}</span>
        <ClearFilters active={anyFilter} onClear={clearFilters} />
      </div>

      {shown.length === 0 && (
        <div style={{ padding: '40px', textAlign: 'center', color: C.muted, fontSize: '13px' }}>
          {!unreadOnly && !typeFilter
            ? 'No notifications yet. Customer actions (like accepting an estimate) will appear here.'
            : 'No notifications match these filters.'}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {shown.map((n) => {
          const meta = TYPE_META[n.type] ?? fallbackMeta;
          const unread = !n.read_at;
          return (
            <div
              key={n.id}
              onClick={() => openLead(n)}
              style={{
                display: 'flex', gap: '12px', alignItems: 'flex-start',
                background: unread ? 'var(--crm-accent-tint)' : C.panel,
                border: `1px solid ${unread ? C.accentSoftLine : C.border}`,
                borderLeft: `3px solid ${meta.color}`,
                borderRadius: '9px', padding: '12px 14px', cursor: 'pointer',
              }}
            >
              <span style={{ display: 'inline-flex', color: meta.color }}><meta.Icon size={18} aria-hidden="true" /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: meta.color }}>{meta.label}</span>
                  {unread && <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: C.accentFill, display: 'inline-block' }} />}
                </div>
                <div style={{ fontSize: '13px', fontWeight: 600, color: C.text, marginTop: '3px' }}>{n.title}</div>
                {n.body && <div style={{ fontSize: '12px', color: C.muted, marginTop: '2px' }}>{n.body}</div>}
                <div style={{ fontSize: '11px', color: C.dim, marginTop: '5px', fontFamily: "'DM Mono', monospace" }}>
                  {formatDateTime(n.created_at)}{n.lead_id ? ' · open lead →' : ''}
                </div>
              </div>
              {unread && (
                <button
                  onClick={(e) => markRead(e, n)}
                  style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: '11px', padding: '2px 4px', whiteSpace: 'nowrap' }}
                >
                  Mark read
                </button>
              )}
            </div>
          );
        })}
      </div>

      {total > shown.length && (
        <div style={{ textAlign: 'center', marginTop: '14px' }}>
          <button
            onClick={() => setVisible(visible + PAGE_SIZE)}
            style={{ padding: '8px 16px', background: C.panel2, border: `1px solid ${C.border}`, color: C.text, borderRadius: '7px', cursor: 'pointer', fontSize: '12px' }}
          >
            Load more ({(total - shown.length).toLocaleString()} older)
          </button>
        </div>
      )}
    </div>
  );
}
